import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import {
  Users, ClipboardList, Receipt, Upload, Loader2, CheckCircle2,
  AlertTriangle, ArrowRight, FileSpreadsheet, Brain, X, RefreshCw,
  History, Undo2, Clock, ChevronDown
} from "lucide-react";
import { Button } from "../components/ui/button";

const TABS = [
  { key: "clients", label: "Clients", icon: Users, desc: "Importez votre base clients (CSV, XLSX, PDF)" },
  { key: "crm", label: "Echanges CRM", icon: ClipboardList, desc: "Importez appels, emails, notes dans les fiches clients" },
  { key: "invoices", label: "Factures", icon: Receipt, desc: "Importez vos factures avec detection statut et doublons" },
  { key: "history", label: "Historique", icon: History, desc: "Consultez l'historique de vos imports et effectuez des rollbacks" },
];

function ImportStepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            i < current ? "bg-green-500 text-white" : i === current ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-500"
          }`}>
            {i < current ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
          </div>
          <span className={`text-xs font-medium hidden sm:inline ${i === current ? "text-[#f7c935]" : "text-slate-500"}`}>{s}</span>
          {i < steps.length - 1 && <div className="w-8 h-px bg-white/10" />}
        </div>
      ))}
    </div>
  );
}

function ImportPanel({ tabKey, api }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("upload"); // upload, analyzing, preview, executing, done
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [dupStrategy, setDupStrategy] = useState("skip");
  const [fileName, setFileName] = useState("");

  const endpoints = {
    clients: { analyze: "/import/clients/analyze", execute: "/import/clients/execute" },
    crm: { analyze: "/import/crm-exchanges/analyze", execute: "/import/crm-exchanges/execute" },
    invoices: { analyze: "/import/invoices/analyze", execute: "/import/invoices/execute" },
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setAnalyzing(true);
    setError("");
    setPhase("analyzing");
    setAnalysis(null);

    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(endpoints[tabKey].analyze, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setAnalysis(res.data);
      if (res.data.mapping?.column_mapping) {
        setColumnMapping(res.data.mapping.column_mapping);
      }
      if (res.data.rows) {
        setRows(res.data.rows);
      }
      setPhase("preview");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'analyse");
      setPhase("upload");
    }
    setAnalyzing(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleExecute = async () => {
    setExecuting(true);
    setError("");
    setPhase("executing");

    try {
      const payload = {
        rows: rows,
        column_mapping: columnMapping,
        duplicate_strategy: dupStrategy,
        client_matches: {},
      };

      const res = await api.post(endpoints[tabKey].execute, payload, { timeout: 120000 });
      setResult(res.data);
      setPhase("done");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'import");
      setPhase("preview");
    }
    setExecuting(false);
  };

  const reset = () => {
    setPhase("upload");
    setAnalysis(null);
    setResult(null);
    setError("");
    setRows([]);
    setColumnMapping({});
    setFileName("");
  };

  const stepLabels = ["Upload", "Analyse IA", "Verification", "Import"];
  const currentStep = phase === "upload" ? 0 : phase === "analyzing" ? 1 : phase === "preview" ? 2 : phase === "executing" ? 3 : 3;

  return (
    <div data-testid={`import-panel-${tabKey}`}>
      <ImportStepIndicator steps={stepLabels} current={currentStep} />

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-sm text-red-400">{error}</span>
          <button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      {/* Phase: Upload */}
      {phase === "upload" && (
        <div className="text-center py-12">
          <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: "rgba(247,201,53,0.1)" }}>
            <FileSpreadsheet className="w-10 h-10" style={{ color: "#f7c935" }} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">
            {tabKey === "clients" ? "Importez votre base clients" : tabKey === "crm" ? "Importez vos echanges CRM" : "Importez vos factures"}
          </h3>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
            Deposez un fichier CSV, XLSX ou PDF. L'IA analysera automatiquement la structure et mappera les colonnes.
          </p>
          <input ref={fileRef} type="file" onChange={handleUpload} accept=".csv,.xlsx,.xls,.pdf" className="hidden" data-testid={`import-file-${tabKey}`} />
          <Button onClick={() => fileRef.current?.click()} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold px-8 py-3" data-testid={`import-upload-btn-${tabKey}`}>
            <Upload className="w-5 h-5 mr-2" /> Choisir un fichier
          </Button>
          <p className="text-xs text-slate-600 mt-3">Formats acceptes : CSV, XLSX, PDF</p>
        </div>
      )}

      {/* Phase: Analyzing */}
      {phase === "analyzing" && (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center animate-pulse" style={{ background: "rgba(247,201,53,0.15)" }}>
            <Brain className="w-8 h-8" style={{ color: "#f7c935" }} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Analyse IA en cours...</h3>
          <p className="text-sm text-slate-400">L'IA analyse la structure de <strong>{fileName}</strong></p>
          <Loader2 className="w-6 h-6 animate-spin mx-auto mt-4" style={{ color: "#f7c935" }} />
        </div>
      )}

      {/* Phase: Preview */}
      {phase === "preview" && analysis && (
        <div className="space-y-6">
          {/* Analysis summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border p-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <div className="text-xl font-black" style={{ color: "#f7c935" }}>{analysis.row_count || 0}</div>
              <div className="text-[10px] text-slate-500">Lignes detectees</div>
            </div>
            <div className="rounded-xl border p-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <div className="text-xl font-black text-blue-400">{Object.keys(columnMapping).length}</div>
              <div className="text-[10px] text-slate-500">Colonnes mappees</div>
            </div>
            <div className="rounded-xl border p-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <div className="text-xl font-black text-green-400">{Math.round((analysis.mapping?.confidence || 0) * 100)}%</div>
              <div className="text-[10px] text-slate-500">Confiance IA</div>
            </div>
            {analysis.duplicates_found > 0 && (
              <div className="rounded-xl border p-3 border-orange-500/20" style={{ background: "rgba(249,115,22,0.05)" }}>
                <div className="text-xl font-black text-orange-400">{analysis.duplicates_found}</div>
                <div className="text-[10px] text-orange-400">Doublons detectes</div>
              </div>
            )}
            {analysis.mapping?.potential_duplicates?.length > 0 && (
              <div className="rounded-xl border p-3 border-orange-500/20" style={{ background: "rgba(249,115,22,0.05)" }}>
                <div className="text-xl font-black text-orange-400">{analysis.mapping.potential_duplicates.length}</div>
                <div className="text-[10px] text-orange-400">Doublons factures</div>
              </div>
            )}
          </div>

          {/* Column mapping */}
          <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
            <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Brain className="w-4 h-4" style={{ color: "#f7c935" }} /> Mapping des colonnes (IA)
            </h4>
            <div className="grid md:grid-cols-2 gap-2">
              {Object.entries(columnMapping).map(([src, tgt]) => (
                <div key={src} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02]">
                  <span className="text-xs text-slate-400 flex-1 truncate">{src}</span>
                  <ArrowRight className="w-3 h-3 text-slate-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-[#f7c935] flex-1 truncate">{tgt}</span>
                </div>
              ))}
            </div>
            {analysis.mapping?.unmapped_columns?.length > 0 && (
              <p className="text-[10px] text-slate-600 mt-2">Colonnes ignorees : {analysis.mapping.unmapped_columns.join(", ")}</p>
            )}
          </div>

          {/* Preview data */}
          {analysis.mapping?.preview?.length > 0 && (
            <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
              <h4 className="text-sm font-bold text-white mb-3">Apercu des donnees mappees</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {Object.keys(analysis.mapping.preview[0] || {}).map(k => (
                        <th key={k} className="text-left px-2 py-1.5 text-slate-500 font-medium uppercase text-[10px]">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.mapping.preview.map((row, i) => (
                      <tr key={i} className="border-t border-white/5">
                        {Object.values(row).map((v, j) => (
                          <td key={j} className="px-2 py-1.5 text-slate-300 truncate max-w-[200px]">{String(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Duplicate strategy */}
          {(analysis.duplicates_found > 0 || analysis.mapping?.potential_duplicates?.length > 0) && (
            <div className="rounded-xl border p-4 border-orange-500/20" style={{ background: "rgba(249,115,22,0.05)" }}>
              <h4 className="text-sm font-bold text-orange-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Gestion des doublons
              </h4>
              <div className="flex gap-3">
                {[
                  { key: "skip", label: "Ignorer les doublons", desc: "Ne pas importer les lignes en doublon" },
                  { key: "update", label: "Mettre a jour", desc: "Mettre a jour les fiches existantes" },
                ].map(opt => (
                  <button key={opt.key} onClick={() => setDupStrategy(opt.key)}
                    className={`flex-1 p-3 rounded-lg border text-left transition-colors ${dupStrategy === opt.key ? "border-[#f7c935]/40 bg-[#f7c935]/5" : "border-white/5 hover:border-white/10"}`}
                    data-testid={`dup-strategy-${opt.key}`}>
                    <div className={`text-xs font-bold ${dupStrategy === opt.key ? "text-[#f7c935]" : "text-slate-400"}`}>{opt.label}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button onClick={reset} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="import-reset-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Recommencer
            </Button>
            <Button onClick={handleExecute} disabled={executing} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold px-8" data-testid={`import-execute-btn-${tabKey}`}>
              {executing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Lancer l'import
            </Button>
          </div>
        </div>
      )}

      {/* Phase: Executing */}
      {phase === "executing" && (
        <div className="text-center py-16">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: "#f7c935" }} />
          <h3 className="text-lg font-bold text-white mb-2">Import en cours...</h3>
          <p className="text-sm text-slate-400">Traitement, classification et integration des donnees</p>
        </div>
      )}

      {/* Phase: Done */}
      {phase === "done" && result && (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center bg-green-500/15">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-lg font-bold text-white mb-4">Import termine !</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-lg mx-auto mb-6">
            {result.imported > 0 && (
              <div className="rounded-xl border p-3 border-green-500/20 bg-green-500/5">
                <div className="text-xl font-black text-green-400">{result.imported}</div>
                <div className="text-[10px] text-green-400">Importes</div>
              </div>
            )}
            {(result.skipped > 0 || result.skipped_duplicates > 0) && (
              <div className="rounded-xl border p-3 border-orange-500/20 bg-orange-500/5">
                <div className="text-xl font-black text-orange-400">{result.skipped || result.skipped_duplicates}</div>
                <div className="text-[10px] text-orange-400">Doublons ignores</div>
              </div>
            )}
            {result.updated > 0 && (
              <div className="rounded-xl border p-3 border-blue-500/20 bg-blue-500/5">
                <div className="text-xl font-black text-blue-400">{result.updated}</div>
                <div className="text-[10px] text-blue-400">Mis a jour</div>
              </div>
            )}
            {(result.unmatched > 0 || result.unmatched_clients > 0) && (
              <div className="rounded-xl border p-3 border-red-500/20 bg-red-500/5">
                <div className="text-xl font-black text-red-400">{result.unmatched || result.unmatched_clients}</div>
                <div className="text-[10px] text-red-400">Non matches</div>
              </div>
            )}
          </div>
          {result.errors?.length > 0 && (
            <div className="text-left max-w-lg mx-auto mb-4 rounded-lg border border-red-500/20 bg-red-500/5 p-3">
              <div className="text-xs font-bold text-red-400 mb-1">Erreurs ({result.errors.length})</div>
              {result.errors.slice(0, 5).map((e, i) => <div key={i} className="text-[10px] text-red-300">{e}</div>)}
            </div>
          )}
          <Button onClick={reset} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="import-new-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Nouvel import
          </Button>
        </div>
      )}
    </div>
  );
}

const TYPE_LABELS = { clients: "Clients", crm_exchanges: "Echanges CRM", invoices: "Factures" };
const TYPE_COLORS = { clients: "text-blue-400 bg-blue-500/10", crm_exchanges: "text-green-400 bg-green-500/10", invoices: "text-purple-400 bg-purple-500/10" };

function ImportHistoryPanel({ api }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rollingBack, setRollingBack] = useState(null);
  const [confirmRollback, setConfirmRollback] = useState(null);
  const [rollbackResult, setRollbackResult] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/imports/history");
      setSessions(res.data || []);
    } catch { setSessions([]); }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleRollback = async (sessionId) => {
    setRollingBack(sessionId);
    setRollbackResult(null);
    try {
      const res = await api.post(`/imports/rollback/${sessionId}`);
      setRollbackResult({ success: true, message: res.data.message, deleted: res.data.deleted_count });
      setConfirmRollback(null);
      loadHistory();
    } catch (err) {
      setRollbackResult({ success: false, message: err.response?.data?.detail || "Erreur lors du rollback" });
    }
    setRollingBack(null);
  };

  if (loading) return (
    <div className="text-center py-16">
      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: "#f7c935" }} />
      <p className="text-sm text-slate-400">Chargement de l'historique...</p>
    </div>
  );

  return (
    <div data-testid="import-history-panel">
      {rollbackResult && (
        <div className={`flex items-center gap-2 p-3 mb-4 rounded-lg border ${rollbackResult.success ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}`}>
          {rollbackResult.success ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
          <span className={`text-sm ${rollbackResult.success ? "text-green-400" : "text-red-400"}`}>{rollbackResult.message}</span>
          <button onClick={() => setRollbackResult(null)} className="ml-auto"><X className="w-4 h-4 text-slate-400" /></button>
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="text-center py-16">
          <History className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="text-lg font-bold text-white mb-2">Aucun import effectue</h3>
          <p className="text-sm text-slate-500">Vos imports apparaitront ici avec la possibilite de les annuler.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">{sessions.length} session(s) d'import</span>
            <Button onClick={loadHistory} size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5 h-7 text-xs" data-testid="refresh-history">
              <RefreshCw className="w-3 h-3 mr-1" /> Rafraichir
            </Button>
          </div>
          {sessions.map(s => {
            const isRolledBack = s.rolled_back || s.status === "rolled_back";
            return (
              <div key={s.session_id} className={`rounded-xl border p-4 transition-colors ${isRolledBack ? "opacity-60" : ""}`}
                style={{ background: "var(--bg-card)", borderColor: isRolledBack ? "rgba(239,68,68,0.2)" : "var(--border-primary)" }}
                data-testid={`history-row-${s.session_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${TYPE_COLORS[s.type] || "bg-slate-500/10 text-slate-400"}`}>
                    {s.type === "clients" ? <Users className="w-5 h-5" /> : s.type === "crm_exchanges" ? <ClipboardList className="w-5 h-5" /> : <Receipt className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-white">{TYPE_LABELS[s.type] || s.type}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${isRolledBack ? "bg-red-500/10 text-red-400" : "bg-green-500/10 text-green-400"}`}>
                        {isRolledBack ? "Annule" : "Actif"}
                      </span>
                      {s.imported > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f7c935]/10 text-[#f7c935] font-medium">{s.imported} importes</span>}
                      {(s.skipped > 0 || s.skipped_duplicates > 0) && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-400 font-medium">{s.skipped || s.skipped_duplicates} ignores</span>}
                      {s.errors_count > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">{s.errors_count} erreurs</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {new Date(s.created_at).toLocaleString("fr-FR")}
                      {s.user_name && <span>| par {s.user_name}</span>}
                      <span className="text-slate-600">| {s.total_rows} lignes traitees</span>
                    </div>
                    {isRolledBack && s.rolled_back_at && (
                      <div className="text-[10px] text-red-400/70 mt-0.5">
                        Annule le {new Date(s.rolled_back_at).toLocaleString("fr-FR")} — {s.deleted_count || 0} supprime(s)
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {!isRolledBack && s.imported > 0 && (
                      confirmRollback === s.session_id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-red-400 font-medium">Confirmer ?</span>
                          <Button size="sm" onClick={() => handleRollback(s.session_id)} disabled={rollingBack === s.session_id}
                            className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-7 text-[10px] px-2" data-testid={`confirm-rollback-${s.session_id}`}>
                            {rollingBack === s.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                            Oui, annuler
                          </Button>
                          <Button size="sm" onClick={() => setConfirmRollback(null)} variant="outline" className="border-white/10 h-7 text-[10px] px-2">
                            Non
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" onClick={() => setConfirmRollback(s.session_id)}
                          className="bg-red-500/10 text-red-400 hover:bg-red-500/20 h-8 text-xs" data-testid={`rollback-btn-${s.session_id}`}>
                          <Undo2 className="w-3.5 h-3.5 mr-1" /> Rollback
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ImportPage() {
  const { axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "clients";
  const [tab, setTab] = useState(initialTab);
  const api = axiosAuth();

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: "var(--bg-primary)", color: "var(--text-primary)" }} data-testid="import-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <Brain className="w-7 h-7" style={{ color: "#f7c935" }} />
          <span>Import <span style={{ color: "#f7c935" }}>IA</span></span>
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Importez et classez automatiquement vos donnees grace a l'intelligence artificielle</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl mb-6" style={{ background: "var(--bg-card)" }} data-testid="import-tabs">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`import-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${active ? "shadow-sm" : "hover:opacity-80"}`}
              style={active ? { background: "var(--accent)", color: "#14101c" } : { color: "var(--text-muted)" }}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* Tab description */}
      <div className="mb-6">
        <p className="text-xs" style={{ color: "var(--text-dimmed)" }}>{TABS.find(t => t.key === tab)?.desc}</p>
      </div>

      {/* Import panel or History */}
      <div className="rounded-xl border p-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        {tab === "history" ? (
          <ImportHistoryPanel api={api} />
        ) : (
          <ImportPanel key={tab} tabKey={tab} api={api} />
        )}
      </div>
    </div>
  );
}
